import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import Header from '../components/Header';
import {
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    MAX_FILE_SIZE_BYTES,
    sanitizePdfFile,
} from '../util/fileUpload';
import '../styles/AddTopicForm.css';
import useNewTopicWebSocket from "../hooks/useNewTopicWebSocket";
import PDFConfirmModal from "../components/PDFConfirmModal";
import RemoveCommissionConfirmModal from "../components/RemoveCommissionConfirmModal";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
import { faPenToSquare, faPlus, faChevronLeft, faTrash, faCheck, faChevronDown, faLandmarkFlag } from '@fortawesome/free-solid-svg-icons';
import api from '../api/axios';
import { openPdfTab, sanitizeFilenameForUrl } from '../utils/pdfTab';


const AddTopicForm = () => {
    const { id, idt, municipalityId } = useParams();
    const [title, setTitle] = useState('');
    const [files, setFiles] = useState([]);   
    const [pdfId, setPdfId] = useState(null);
    const [amount, setAmount] = useState('');
    const [fileError, setFileError] = useState(false);
    const [fileTypeError, setFileTypeError] = useState(false);
    const [currentPdfFileName, setCurrentPdfFileName] = useState('');
    const navigate = useNavigate();
    const isAddAfter = !!idt && window.location.pathname.includes('add-after');
    const isAddBefore = !!idt && window.location.pathname.includes('add-before');
    const [exportLoading, setExportLoading] = useState(false);
    const [totalSize, setTotalSize] = useState(0);
    const { t } = useTranslation();
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [dropUp, setDropUp] = useState(false);

    const fileInputRef = useRef(null);
    const { sendNewTopic } = useNewTopicWebSocket(id);

    const [topicStatus, setTopicStatus] = useState('');
    const [availableCommissions, setAvailableCommissions] = useState([]);
    const [selectedCommissionIds, setSelectedCommissionIds] = useState([]);
    const [commissionsExpanded, setCommissionsExpanded] = useState(false);
    const [commissionStatuses, setCommissionStatuses] = useState({});
    const [commissionPendingRemoval, setCommissionPendingRemoval] = useState(null);

    const isEditing = !!idt && !isAddAfter && !isAddBefore;

    const topicStatusOptions = [
        { value: 'CREATED', label: t("addTopicForm.statusOptions.created") },
        { value: 'ACTIVE', label: t("addTopicForm.statusOptions.active") },
        { value: 'FINISHED', label: t("addTopicForm.statusOptions.finished") },
        { value: 'INFORMATION', label: t("addTopicForm.statusOptions.information") },
        { value: 'WITHDRAWN', label: t("addTopicForm.statusOptions.withdrawn") },
    ];

    const createStatusOptions = [
    { value: 'CREATED', label: t("addTopicForm.statusOptions.created") },
    { value: 'INFORMATION', label: t("addTopicForm.statusOptions.information") },
    { value: 'WITHDRAWN', label: t("addTopicForm.statusOptions.withdrawn") },
    ];

    const statusOptions = isEditing ? topicStatusOptions : createStatusOptions;

    // fetch topic if editing
   useEffect(() => {
    if (isAddAfter || isAddBefore) {
        setTopicStatus("CREATED");
        return;
    }

    if (!idt) {
        setTopicStatus("CREATED");
        return;
    }

    const fetchTopic = async () => {
        try {
            const { data: topicData } = await api.get(
                `/api/sessions/${id}/topics/${idt}`
            );

            setTitle(topicData.title);
            setCurrentPdfFileName(topicData.pdfFileName);
            setPdfId(topicData.pdfFileId);
            setTopicStatus(topicData.topicStatus || "CREATED");
            setAmount(topicData.amount ? topicData.amount : '');

        } catch (error) {
            console.error("Error fetching the topic:", error);
        }
    };

    fetchTopic();
    }, [idt, id, isAddAfter, isAddBefore]);

    // fetch commissions available for this session's municipality term
    useEffect(() => {
        const fetchCommissions = async () => {
            try {
                let termId = null;
                const cachedSessions = JSON.parse(localStorage.getItem(`sessions_${municipalityId}`)) || [];
                const cachedSession = cachedSessions.find(s => String(s.id) === String(id));

                if (cachedSession) {
                    termId = cachedSession.municipalityMandateId;
                } else {
                    const { data } = await api.get(`/api/sessions/${id}`);
                    termId = data.municipalityMandateId;
                }

                if (!termId) return;

                const { data: commissions } = await api.get(`/api/municipality-terms/${termId}/commissions`);
                setAvailableCommissions(commissions);
            } catch (error) {
                console.error('Error fetching commissions:', error);
            }
        };

        if (id) fetchCommissions();
    }, [id, municipalityId]);

    // fetch commissions already assigned to this topic when editing
    useEffect(() => {
        if (!isEditing) return;

        const fetchTopicCommissions = async () => {
            try {
                const { data } = await api.get(`/api/topics/${idt}/commissions`);
                setSelectedCommissionIds(data.map(c => String(c.commissionId)));
                setCommissionStatuses(
                    data.reduce((acc, c) => {
                        acc[String(c.commissionId)] = c.status;
                        return acc;
                    }, {})
                );
                if (data.length > 0) setCommissionsExpanded(true);
            } catch (error) {
                console.error('Error fetching topic commissions:', error);
            }
        };

        fetchTopicCommissions();
    }, [isEditing, idt]);

    const removeCommission = (idStr) => {
        setSelectedCommissionIds(prev => prev.filter(c => c !== idStr));
    };

    const toggleCommission = (commissionId) => {
        const idStr = String(commissionId);

        if (selectedCommissionIds.includes(idStr)) {
            const status = commissionStatuses[idStr];
            if (status === 'ACTIVE' || status === 'FINISHED') {
                setCommissionPendingRemoval(idStr);
                return;
            }
            removeCommission(idStr);
        } else {
            setSelectedCommissionIds(prev => [...prev, idStr]);
        }
    };

    const pendingRemovalCommissionName = commissionPendingRemoval
        ? availableCommissions.find(c => String(c.id) === commissionPendingRemoval)?.name
        : '';

    // handle submit
   const handleSubmit = async (e) => {
    e.preventDefault();
    setExportLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("topicStatus", topicStatus);

    if (amount) {
        formData.append("amount", amount);
    }

    files.forEach(file => formData.append("files", file));
    selectedCommissionIds.forEach(cid => formData.append("commissionIds", cid));

    try {
        let endpoint;

        if (isAddAfter) {
        endpoint = `/api/sessions/${id}/topics/add-after/${idt}`;
        } else if (isAddBefore) {
        endpoint = `/api/sessions/${id}/topics/add-before/${idt}`;
        } else if (idt) {
        endpoint = `/api/topics/edit/${idt}`;
        } else {
        endpoint = `/api/sessions/${id}/topic/add`;
        }

        // Municipality is needed only in these cases
        if (!idt || isAddAfter || isAddBefore) {
        formData.append("municipalityId", municipalityId);
        }

        const { data } = await api.post(endpoint, formData);

        const topicId = data.topicId;

        sendNewTopic("NEW_TOPIC");
        sessionStorage.removeItem("scrollPosition");

        navigate(
        `/municipalities/${municipalityId}/sessions/${id}/topics#topic-${topicId}`
        );
    } catch (error) {
        console.error("Error submitting the form:", error);
    } finally {
        setExportLoading(false);
    }
    };


    useEffect(() => {
        const fileDropArea = document.querySelector('.file-drop-area');
        if (!fileDropArea) return;

        const dragOverHandler = (e) => handleDragOver(e, fileDropArea);
        const dragLeaveHandler = () => handleDragLeave(fileDropArea);
        const dropHandler = (e) => handleDrop(e, setFiles, null, setFileError, setFileTypeError, files);
        const pasteHandler = (e) => handlePaste(e, setFiles, null, setFileError, setFileTypeError, files);


        fileDropArea.addEventListener('dragover', dragOverHandler);
        fileDropArea.addEventListener('dragleave', dragLeaveHandler);
        fileDropArea.addEventListener('drop', dropHandler);
        document.addEventListener('paste', pasteHandler);

        return () => {
            fileDropArea.removeEventListener('dragover', dragOverHandler);
            fileDropArea.removeEventListener('dragleave', dragLeaveHandler);
            fileDropArea.removeEventListener('drop', dropHandler);
            document.removeEventListener('paste', pasteHandler);
        };
    }, [files]);

  const validateAndSetFiles = (selectedFiles) => {
    setFileError(false);
    setFileTypeError(false);

    const validFiles = selectedFiles.filter(file => {
        // Check type
        if (file.type !== 'application/pdf') {
            setFileTypeError(true);
            return false;
        }
        // Check individual file size
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setFileError(true);
            return false;
        }
        // Prevent duplicates: same name + size
        const isDuplicate = files.some(f => f.name === file.name && f.size === file.size);
        if (isDuplicate) return false;

        return true;
    }).map(file => {
        return sanitizePdfFile(file);
    });

    const newFiles = [...files, ...validFiles];
    const totalBytes = newFiles.reduce((acc, f) => acc + f.size, 0);

    if (totalBytes > MAX_FILE_SIZE_BYTES) {
        setFileError(true);
        return;
    }

    setFiles(newFiles);
};

    const handleFileInputChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        validateAndSetFiles(selectedFiles);
        if (fileInputRef.current) {
        fileInputRef.current.value = '';
        }
    };

    const handlePdfFetch = async (pdfId) => {
        const newTab = openPdfTab();
        try {
            const { data } = await api.get(`/api/topics/pdf/${pdfId}/name`);
            const token = localStorage.getItem('jwtToken');
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const safeName = sanitizeFilenameForUrl(data.fileName);
            if (newTab && !newTab.closed) {
                newTab.location.href = `${baseUrl}/api/topics/pdf/${pdfId}/${encodeURIComponent(safeName)}?token=${encodeURIComponent(token)}`;
            }
        } catch (error) {
            console.error('Error fetching PDF:', error);
            if (newTab && !newTab.closed) newTab.close();
        }
    };


    useEffect(() => {
        const total = files.reduce((acc, f) => acc + f.size, 0);
        setTotalSize(total);
    }, [files]);

    useEffect(() => {
    // Scroll to top when the component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
}, []);

useEffect(() => {
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setOpen(false); // close the dropdown
        }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
        document.removeEventListener('mousedown', handleClickOutside);
    };
}, []);

const toggleDropdown = () => {
  if (dropdownRef.current) {
    const rect = dropdownRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 200; // max-height of your dropdown in px

    setDropUp(spaceBelow < dropdownHeight);
  }
  setOpen(prev => !prev);
};

const handleRemovePdf = async () => {
    try {
        await api.delete(`/api/topics/${idt}/pdf`);
        setCurrentPdfFileName("");
        setPdfId(null);
        setFiles([]); 
    } catch (error) {
        console.error("Error removing PDF:", error);
    }
};

// At the top of AddTopicForm, after all useState declarations
const textareaRef = useRef(null);
const minRows = 3;
const maxRows = 10;
const lineHeight = 24; // px

const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.rows = minRows; // reset to minimum
    const currentRows = Math.floor(textarea.scrollHeight / lineHeight);

    // Add +1 row ONLY if adding after/before AND NOT editing
    const extraRow = (!isEditing && (isAddAfter || isAddBefore)) ? 1 : 0;

    textarea.rows = Math.min(Math.max(currentRows + extraRow, minRows), maxRows);
}, [isEditing, isAddAfter, isAddBefore]);


// On component mount, resize if editing and title exists
useEffect(() => {
    if (textareaRef.current && title) {
        resizeTextarea();
    }
}, [title, resizeTextarea]);



    return (
        <HelmetProvider>
            <div className="add-topic-container">
                <Helmet>
                    <title>
                        {isAddAfter
                            ? t("addTopicForm.addBelow")
                            : isAddBefore
                            ? t("addTopicForm.addAbove")
                            : idt
                            ? t("addTopicForm.editTitle")
                            : t("addTopicForm.addTitle")}
                    </title>
                </Helmet>

                <Header isSticky={true} />

                <div className="add-topic-body-container">
                    <div>
                        <div className="add-topic-header-div">
                            <h1>
                                {isAddAfter
                                    ? t("addTopicForm.addBelow")
                                    : isAddBefore
                                    ? t("addTopicForm.addAbove")
                                    : idt
                                    ? t("addTopicForm.editTitle")
                                    : t("addTopicForm.addTitle")}
                            </h1>
                        </div>

                        <div className="row justify-content-center">
                            <div>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                            <label htmlFor="title" className="label-add">{t("addTopicForm.topicTitle")}</label>
                                            <textarea
                                                ref={textareaRef}
                                                id="title"
                                                name="title"
                                                className="mb-1 topic-textarea-title"
                                                value={title}
                                                onChange={(e) => {
                                                    const value = e.target.value.slice(0, 700);
                                                    setTitle(value);
                                                    resizeTextarea();
                                                }}
                                                required
                                                placeholder={t("addTopicForm.placeholder")}
                                                rows={minRows}
                                                maxLength={700}
                                            />
                                            <div className="character-counter">
                                                {title.length}/700
                                            </div>
                                        </div>

                                    <label htmlFor="file" className="label-add">{t("addTopicForm.uploadPdf")}</label>
                                    <p className="optional-text">
                                        {t("addTopicForm.optional")}
                                    </p>
                                    <div className="form-group d-flex justify-content-center">
                                        <div className={`file-drop-area ${fileError || fileTypeError ? 'is-active' : ''}`}>
                                            <p className="file-drop-message">
                                                {t('addTopicForm.dropHere')} <span>{t('addTopicForm.orClick')}</span>
                                            </p>
                                            <input
                                                type="file"
                                                id="file"
                                                name="file"
                                                accept="application/pdf"
                                                multiple
                                                ref={fileInputRef}
                                                onChange={handleFileInputChange}
                                            />
                                        </div>
                                    </div>

                                    {fileError && <div className="error-message-pdf text-danger">{t("addTopicForm.maxSizeError")}</div>}
                                    {fileTypeError && <div className="error-message-pdf text-danger">{t("addTopicForm.typeError")}</div>}

                                   {files.length > 0 && (
                                    <div className="uploaded-files mt-3">
                                        <h6>{t("addTopicForm.selectedFiles")}</h6>
                                        <ul className="file-list">
                                            {files.map((f, idx) => (
                                                <li key={idx} className="file-item d-flex align-items-center justify-content-between">
                                                    <span className="file-name">
                                                        {f.name} <span className="file-size">({(f.size / (1024 * 1024)).toFixed(2)} MB)</span>
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn ms-2"
                                                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                                                        >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="total-size mt-1 text-muted">
                                            {(totalSize / (1024*1024)).toFixed(2)} MB / {(MAX_FILE_SIZE_BYTES / (1024*1024)).toFixed(0)} MB
                                        </div>
                                    </div>
                                )}

                                {currentPdfFileName && (
                                    <div className="current-pdf-div mt-2 d-flex align-items-center">
                                        <span className="current-pdf-label me-2">
                                            {t("addTopicForm.currentPdf")}:
                                        </span>
                                        <span
                                            className="pdf-link me-2"
                                            onClick={() => handlePdfFetch(pdfId)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            {currentPdfFileName}
                                        </span>
                                        <button
                                            type="button"
                                            className="remove-pdf-button"
                                            onClick={() => setIsPdfModalOpen(true)}
                                        >
                                            {t("addTopicForm.removePdf")} {" "}
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </div>
                                )}

                                   <div className="topic-status-select-wrapper mt-3" ref={dropdownRef}>
                                        <label htmlFor="topicStatus" className="label-add">{t("addTopicForm.selectStatus")}</label>

                                        <div
                                            className="custom-select-box"
                                            onClick={toggleDropdown}
                                            tabIndex={0}
                                        >
                                            {statusOptions.find((o) => o.value === topicStatus)?.label || "Select Status"}
                                        </div>

                                        {open && (
                                            <div className={`custom-options ${dropUp ? 'drop-up' : ''}`}>
                                                {statusOptions.map((option) => (
                                                    <div
                                                        key={option.value}
                                                        className={`custom-option ${topicStatus === option.value ? "selected" : ""}`}
                                                        onClick={() => {
                                                            setTopicStatus(option.value);
                                                            setOpen(false);
                                                        }}
                                                    >
                                                        {option.label}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group mt-3">
                                        <label htmlFor="amount" className="label-add">{t("addTopicForm.amount")}</label>
                                        <p className="optional-text">
                                            {t("addTopicForm.optional")}
                                        </p>
                                        <input
                                            type="number"
                                            id="amount"
                                            name="amount"
                                            className="topic-amount-input"
                                            value={amount}
                                            onChange={(e) => {
                                                // Remove anything invalid
                                                let value = e.target.value;

                                                // Prevent empty string or 0
                                                if (value === '' || Number(value) <= 0) {
                                                    setAmount('');
                                                    return;
                                                }

                                                setAmount(value);
                                            }}
                                            onKeyDown={(e) => {
                                                // Prevent typing e, E, +, - 
                                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            placeholder={t("addTopicForm.amountPlaceholder")}
                                            min={1}
                                            step={1}
                                        />

                                    </div>

                                    {availableCommissions.length > 0 && (
                                        <div className="form-group mt-3 topic-commissions-section">
                                            <button
                                                type="button"
                                                className="topic-commissions-toggle"
                                                onClick={() => setCommissionsExpanded(prev => !prev)}
                                                aria-expanded={commissionsExpanded}
                                            >
                                                <span className="topic-commissions-toggle-left">
                                                    <FontAwesomeIcon icon={faLandmarkFlag} className="topic-commissions-toggle-icon" />
                                                    <span className="topic-commissions-toggle-label">
                                                        {t("addTopicForm.commissions")}
                                                    </span>
                                                    <span className="optional-text topic-commissions-toggle-optional">
                                                        {t("addTopicForm.optional")}
                                                    </span>
                                                    {selectedCommissionIds.length > 0 && (
                                                        <span className="topic-commissions-count-badge">
                                                            {selectedCommissionIds.length}
                                                        </span>
                                                    )}
                                                </span>
                                                <FontAwesomeIcon
                                                    icon={faChevronDown}
                                                    className={`topic-commissions-chevron ${commissionsExpanded ? 'topic-commissions-chevron--open' : ''}`}
                                                />
                                            </button>

                                            <div className={`topic-commissions-accordion ${commissionsExpanded ? 'topic-commissions-accordion--open' : ''}`}>
                                                <div className="topic-commissions-accordion-inner">
                                                    <div className="topic-commissions-grid">
                                                        {availableCommissions.map(commission => {
                                                            const checked = selectedCommissionIds.includes(String(commission.id));
                                                            return (
                                                                <label
                                                                    key={commission.id}
                                                                    className={`topic-commission-chip ${checked ? 'topic-commission-chip--checked' : ''}`}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        className="topic-commission-chip-input"
                                                                        checked={checked}
                                                                        onChange={() => toggleCommission(commission.id)}
                                                                    />
                                                                    <span className="topic-commission-chip-box">
                                                                        <FontAwesomeIcon icon={faCheck} className="topic-commission-chip-check" />
                                                                    </span>
                                                                    <span className="topic-commission-chip-label">{commission.name}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-3 d-flex flex-start">
                                        <button
                                            type="submit"
                                            className="add-form-submit-button me-2"
                                        >
                                            {idt && !isAddAfter && !isAddBefore ? (
                                                <>
                                                    <FontAwesomeIcon icon={faPenToSquare} className="me-2" />
                                                    {t("common.edit")}
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                    {t("common.add")}
                                                </>
                                            )}
                                        </button>

                                        <button
                                            type="button"
                                            className="add-form-back-button"
                                            onClick={() =>
                                                navigate(
                                                    idt
                                                        ? `/municipalities/${municipalityId}/sessions/${id}/topics#topic-${idt}`
                                                        : `/municipalities/${municipalityId}/sessions/${id}/topics`
                                                )
                                            }
                                        >
                                            <span className="back-icon">
                                                <FontAwesomeIcon icon={faChevronLeft} />
                                            </span>
                                            {t("common.back")}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                    <p>&nbsp;</p>
                {exportLoading && (
                    <div className="modal-overlay">
                        <div className="export-loading-spinner">
                            <img src={`${process.env.PUBLIC_URL}/images/loading.svg`} alt="Export Loading..." />
                        </div>
                    </div>
                )}

                <PDFConfirmModal
                    isOpen={isPdfModalOpen}
                    pdfName={currentPdfFileName}
                    onClose={() => setIsPdfModalOpen(false)}
                    onConfirm={async () => {
                        await handleRemovePdf();
                        setIsPdfModalOpen(false);
                    }}
                />

                <RemoveCommissionConfirmModal
                    isOpen={!!commissionPendingRemoval}
                    commissionName={pendingRemovalCommissionName}
                    onClose={() => setCommissionPendingRemoval(null)}
                    onConfirm={() => {
                        removeCommission(commissionPendingRemoval);
                        setCommissionPendingRemoval(null);
                    }}
                />
            </div>
        </HelmetProvider>
    );
};

export default AddTopicForm;